import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Matches the first number in a string, keeping dot/comma/space group separators
 * (e.g. `97`, `1.250`, `12 500`).
 */
const NUMBER_PATTERN = /\d+(?:[,. ]\d+)*/;

/**
 * Approximation of the `cubic-bezier(0.16, 1, 0.3, 1)` easing used by the previous theme.
 *
 * @param {number} t - Linear progress between 0 and 1.
 * @returns {number} Eased progress between 0 and 1.
 */
function easeOutExpo(t) {
  return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
}

/**
 * Renders an impact number. The final value is always present in the markup, so the component is
 * purely decorative: without JS, with reduced motion, or without the `count-up` attribute the text
 * stays exactly as rendered by Liquid.
 */
class ImpactText extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (!this.hasAttribute('count-up') || prefersReducedMotion()) return;

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];

        if (!entry?.isIntersecting) return;

        this.#intersectionObserver?.disconnect();
        this.#countUp();
      },
      { threshold: 0.3 }
    );

    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver?.disconnect();

    if (this.#animationFrame != null) cancelAnimationFrame(this.#animationFrame);
  }

  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  /** @type {number | null} */
  #animationFrame = null;

  /**
   * Animates the first number found in the text from zero to its final value, preserving any
   * surrounding characters (`%`, `+`, currency, …) and group separators.
   */
  #countUp() {
    const text = this.textContent?.trim();
    const match = text?.match(NUMBER_PATTERN);

    if (!text || !match) return;

    const finalValue = match[0];
    const target = parseInt(finalValue.replace(/[,. ]/g, ''), 10);

    if (!Number.isFinite(target)) return;

    // Wrap the number so only it is repainted, and reserve its final width up front to avoid layout
    // shift while the digits change.
    const numberSpan = document.createElement('span');
    numberSpan.className = 'impact-text__number';
    numberSpan.textContent = finalValue;

    const before = text.slice(0, match.index);
    const after = text.slice((match.index ?? 0) + finalValue.length);

    this.replaceChildren(before, numberSpan, after);
    numberSpan.style.minWidth = `${numberSpan.getBoundingClientRect().width}px`;

    const separators = [...finalValue.matchAll(/[,. ]/g)];
    const duration = (parseFloat(this.getAttribute('count-up') || '') || 2) * 1000;
    const start = performance.now();

    /** @param {number} now */
    const step = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = easeOutExpo(progress);

      if (progress === 1) {
        numberSpan.textContent = finalValue;
        numberSpan.style.minWidth = '';
        this.#animationFrame = null;
        return;
      }

      let formatted = Math.round(eased * target).toString();

      // Re-insert the group separators once the value is long enough to need them.
      for (const separator of separators) {
        const index = separator.index ?? 0;

        if (formatted.length >= finalValue.length - index) {
          formatted = formatted.slice(0, index) + separator[0] + formatted.slice(index);
        }
      }

      numberSpan.textContent = formatted;
      this.#animationFrame = requestAnimationFrame(step);
    };

    this.#animationFrame = requestAnimationFrame(step);
  }
}

// Register once
if (!customElements.get('impact-text')) {
  customElements.define('impact-text', ImpactText);
}
